import type { Permission } from '@mntn-dev/authorization-types'
import {
  OrganizationId,
  SessionId,
  TeamId,
  UserId,
  UserUrn,
} from '@mntn-dev/domain-types'
import type { OverrideProperties, SetOptional, Simplify } from 'type-fest'
import type { Auth0Session, Auth0User } from './auth0-session.ts'
import type { SessionPrincipal } from './principal.ts'

type AuthenticatedSession = Simplify<
  SetOptional<
    OverrideProperties<
      Auth0Session,
      { user: SetOptional<Auth0User, 'sub'> | Record<string, never> }
    >,
    'internal' | 'tokenSet'
  > & {
    sessionId: SessionId
    principal: SessionPrincipal
    accessTokenExpiresAt?: number
    accessToken?: string
  }
>

/**
 * UnauthenticatedSession
 */

type UnauthenticatedSession =
  | OverrideProperties<
      SetOptional<
        AuthenticatedSession,
        'principal' | 'internal' | 'tokenSet' | 'user'
      >,
      { user?: SetOptional<Auth0User, 'sub'> | Record<string, never> }
    >
  | null
  | undefined

/**
 * AuthorizedSession
 */

type AuthorizedSessionJSON = AuthenticatedSession

type AuthorizedSession = OverrideProperties<
  AuthorizedSessionJSON,
  {
    principal: AuthorizedSessionJSON['principal'] & {
      permissions: Permission[]
    }
  }
>

type SystemSessionJson = AuthorizedSessionJSON
type SystemSession = AuthorizedSession

/**
 * Example of AuthorizedSession below
 */

const _authorizedSessionExample: AuthorizedSession = {
  sessionId: SessionId(),
  principal: {
    userId: UserId(),
    emailAddress: 'test@mountain.com',
    teamIds: [TeamId()],
    organizationId: OrganizationId(),
    organizationType: 'brand',
    privileges: ['core', 'brand'],
    user: {
      userId: UserId(),
      userUrn: UserUrn(UserId()),
      emailAddress: 'test@mountain.com',
      organizationId: OrganizationId(),
      organizationType: 'brand',
      firstName: 'Some',
      lastName: 'User',
      displayName: 'Some User',
      initials: 'SU',
      authConnection: 'Username-Password-Authentication',
      isActive: true,
      signUpTimestamp: new Date(),
    },
    scopes: [],
    permissions: ['project:view'],
    isActive: true,
  },
  user: {
    sub: '',
  },
}

export type {
  UnauthenticatedSession,
  AuthenticatedSession,
  AuthorizedSession,
  AuthorizedSessionJSON,
  SystemSession,
  SystemSessionJson,
}
